/**
 * Safe Error Log Serializer — PR-AUDIT-02A / PR-LOG-02 runtime migration
 *
 * Canonical structured error logging with safe redaction.
 *
 * Rules:
 *   - No raw error object spread
 *   - No raw stack in production logs
 *   - No raw error.message unless sanitized
 *   - Stable error codes
 *   - Unknown errors fail closed
 *   - Nested secret redaction
 *   - Circular object safety
 *   - Maximum serialized length (UTF-8 bytes, always valid JSON)
 *   - Public response and internal log separated
 *   - Internal audit event contains no credentials
 *   - No full file contents, prompts or provider responses in logs
 *   - requestId/actorId/tenantId strictly sanitized (no log injection)
 *   - URL query secrets redacted
 *
 * Reuses existing redaction utilities from packages/core/src/audit/redaction.ts
 * and is consumed by server, desktop main, MCP, provider and audit-sync call sites.
 */
import { isSensitiveKey, redactSensitiveData } from "./redaction.js";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
/** Maximum character length of safeMessage in a SafeErrorLog */
export const SAFE_ERROR_MESSAGE_MAX_LEN = 256;
/** Maximum total serialized size of a SafeErrorLog (UTF-8 bytes) */
export const SAFE_ERROR_LOG_MAX_BYTES = 4096;
/** Maximum character length of a message exposed on a public surface */
export const SAFE_PUBLIC_MESSAGE_MAX_LEN = 200;
/** Maximum length of requestId / actorId / tenantId */
export const SAFE_ID_MAX_LEN = 128;
/** Placeholder for redacted values */
export const SAFE_ERROR_REDACTED = "[REDACTED]";
/** Placeholder for truncated values */
export const SAFE_ERROR_TRUNCATED = "[TRUNCATED]";
/** Generic public-facing error message — never contains internal diagnostics */
export const SAFE_PUBLIC_ERROR_GENERIC_MESSAGE = "Internal request failed.";
// ---------------------------------------------------------------------------
// Patterns for inline secret detection in strings
// ---------------------------------------------------------------------------
const SECRET_INLINE_PATTERNS = [
    /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, // PEM private key blocks
    /(?:Authorization|Cookie|Set-Cookie)\s*:\s*[\S][^\n]*/gi, // Auth headers (multi-part)
    /Bearer\s+[A-Za-z0-9_.-]{8,}/gi, // Bearer tokens
    /(?:password|passwd|secret|apikey|api_key|token|auth[-_]?key)\s*[:=]\s*\S+/gi, // key=value secrets
    /(?:https?:\/\/)[^:@\s]+:[^@\s]+@/gi, // URL basic-auth credentials
    /(?:postgres(?:ql)?|mysql|mongodb(?:\+srv)?|redis|mariadb):\/\/[^:@\s]+:[^@\s]+@/gi, // DSN / DB URL credentials
    /sk-[A-Za-z0-9_-]{6,}/g, // OpenAI/Anthropic API keys
    /AIza[a-zA-Z0-9_-]{12,}/g, // Google API keys
    /ghp_[A-Za-z0-9_]{36,}/g, // GitHub PATs
    /xox[baprs]-[a-zA-Z0-9-]{12,}/g, // Slack tokens
];
/**
 * URL query parameter names that must be redacted.
 * Matches both plain and URL-encoded variants.
 */
const URL_QUERY_SECRET_KEYS = new Set([
    "token",
    "access_token",
    "accesstoken",
    "api_key",
    "apikey",
    "password",
    "passwd",
    "secret",
    "refresh_token",
    "refreshtoken",
    "sig",
    "signature",
    "x-amz-signature",
    "x-goog-signature",
]);
/**
 * Redact sensitive query parameters from a URL string.
 * Handles both raw and URL-encoded parameter names.
 * Replaces the value portion with [REDACTED].
 */
export function redactUrlQuerySecrets(url) {
    return url.replace(/([?&])([\w%.-]+)(=)([^&\s#]*)/g, (_match, sep, rawKey, eq, _val) => {
        let key;
        try {
            key = decodeURIComponent(rawKey).toLowerCase();
        }
        catch {
            key = rawKey.toLowerCase();
        }
        if (URL_QUERY_SECRET_KEYS.has(key)) {
            return `${sep}${rawKey}${eq}${SAFE_ERROR_REDACTED}`;
        }
        return _match;
    });
}
/**
 * Redact inline secrets from a string value.
 * Applies URL query secret redaction first, then pattern-based redaction.
 * Replaces matched patterns with [REDACTED].
 */
export function redactInlineSecrets(value) {
    let result = redactUrlQuerySecrets(value);
    for (const pattern of SECRET_INLINE_PATTERNS) {
        const p = new RegExp(pattern.source, pattern.flags);
        result = result.replace(p, SAFE_ERROR_REDACTED);
    }
    return result;
}
// ---------------------------------------------------------------------------
// Error serialization
// ---------------------------------------------------------------------------
/**
 * Safely extract a message from an unknown thrown value.
 * Never returns raw stack or credential-containing strings.
 */
export function extractSafeMessage(error) {
    if (error === null || error === undefined) {
        return "Unknown error (null/undefined thrown)";
    }
    if (typeof error === "string") {
        return truncateSafeMessage(redactInlineSecrets(error));
    }
    if (typeof error === "number" || typeof error === "boolean") {
        return `Thrown primitive: ${String(error)}`;
    }
    if (typeof error === "bigint") {
        return `Thrown primitive: ${String(error)}`;
    }
    if (typeof error === "symbol") {
        return `Thrown primitive: ${error.toString()}`;
    }
    if (error instanceof Error) {
        const sanitized = redactInlineSecrets(error.message);
        return truncateSafeMessage(sanitized);
    }
    if (typeof error === "object") {
        try {
            const obj = error;
            const msg = typeof obj["message"] === "string"
                ? redactInlineSecrets(obj["message"])
                : "Unknown error object";
            return truncateSafeMessage(msg);
        }
        catch {
            return "Unknown error (serialization failed)";
        }
    }
    return "Unknown error";
}
/**
 * Canonical sanitized message for public API/IPC surfaces.
 *
 * Compared with a raw `err.message` / `String(err)` interpolation this:
 *   - only ever exposes text that came from an `Error` message or a thrown string;
 *   - replaces every other thrown value (objects, primitives, null) with the
 *     generic fallback instead of echoing arbitrary serialized content;
 *   - redacts inline secrets and bounds the length to
 *     SAFE_PUBLIC_MESSAGE_MAX_LEN characters.
 */
export function toSafePublicMessage(error, fallback = SAFE_PUBLIC_ERROR_GENERIC_MESSAGE) {
    let raw;
    if (typeof error === "string") {
        raw = error;
    }
    else if (error instanceof Error) {
        raw = error.message;
    }
    if (raw === undefined)
        return fallback;
    const sanitized = redactInlineSecrets(raw).trim();
    if (sanitized.length === 0)
        return fallback;
    return sanitized.length > SAFE_PUBLIC_MESSAGE_MAX_LEN
        ? sanitized.slice(0, SAFE_PUBLIC_MESSAGE_MAX_LEN) + SAFE_ERROR_TRUNCATED
        : sanitized;
}
/**
 * Truncate a message to SAFE_ERROR_MESSAGE_MAX_LEN characters.
 */
export function truncateSafeMessage(msg) {
    if (msg.length <= SAFE_ERROR_MESSAGE_MAX_LEN)
        return msg;
    return msg.slice(0, SAFE_ERROR_MESSAGE_MAX_LEN) + SAFE_ERROR_TRUNCATED;
}
// ---------------------------------------------------------------------------
// Circular-safe serialization — always returns valid JSON
// ---------------------------------------------------------------------------
/**
 * Serialize an unknown value to JSON safely.
 *
 * Guarantees:
 *   - ALWAYS returns a string that is valid, parseable JSON.
 *   - Handles circular references (replaced with "[Circular]").
 *   - Handles top-level undefined, function, symbol → returns
 *     {"error":"non-serializable","type":"<typeof>"} (never the bare
 *     undefined that JSON.stringify would produce for these types).
 *   - Handles top-level BigInt → returns
 *     {"error":"non-serializable","type":"bigint"}.
 *   - Enforces maximum UTF-8 byte length; when exceeded returns a
 *     wrapper object: {"truncated":true,"reason":"max_bytes","preview":"..."}.
 */
export function safeJsonSerialize(value, maxBytes = SAFE_ERROR_LOG_MAX_BYTES) {
    // Handle top-level non-serializable types that JSON.stringify returns
    // undefined for (which is NOT valid JSON).
    if (value === undefined ||
        typeof value === "function" ||
        typeof value === "symbol" ||
        typeof value === "bigint") {
        return JSON.stringify({ error: "non-serializable", type: typeof value });
    }
    const seen = new WeakSet();
    let full;
    try {
        const candidate = JSON.stringify(value, (_key, val) => {
            if (typeof val === "bigint") {
                // BigInt inside an object — convert to string to avoid throw
                return `[BigInt:${String(val)}]`;
            }
            if (typeof val === "function" || typeof val === "symbol") {
                return `[${typeof val}]`;
            }
            if (typeof val === "object" && val !== null) {
                if (seen.has(val))
                    return "[Circular]";
                seen.add(val);
            }
            return val;
        });
        // JSON.stringify returns undefined when top-level value is undefined/function/symbol.
        // We already guard those above, but be defensive.
        full = candidate ?? JSON.stringify({ error: "non-serializable", type: typeof value });
    }
    catch {
        full = JSON.stringify({ error: "Serialization failed" });
    }
    // Measure UTF-8 byte length
    const byteLen = new TextEncoder().encode(full).length;
    if (byteLen <= maxBytes)
        return full;
    // Truncate: build a safe preview that fits within maxBytes.
    // The wrapper object {"truncated":true,"reason":"max_bytes","preview":"..."} is
    // always valid JSON regardless of what preview contains (it is JSON-stringified).
    const wrapperOverhead = JSON.stringify({
        truncated: true,
        reason: "max_bytes",
        preview: "",
    }).length;
    const previewMaxChars = Math.max(0, maxBytes - wrapperOverhead - 10);
    // Safely slice preview — avoid cutting in the middle of a multibyte character
    let preview = full.slice(0, previewMaxChars);
    while (new TextEncoder().encode(preview).length > previewMaxChars && preview.length > 0) {
        preview = preview.slice(0, preview.length - 1);
    }
    return JSON.stringify({
        truncated: true,
        reason: "max_bytes",
        preview,
    });
}
// ---------------------------------------------------------------------------
// ID sanitization
// ---------------------------------------------------------------------------
/**
 * Strict allowlist for requestId / actorId / tenantId.
 *
 * Allowed characters: A-Z a-z 0-9 hyphen(-) underscore(_) colon(:) dot(.)
 * Maximum length: SAFE_ID_MAX_LEN (128).
 *
 * REJECTION policy — the ENTIRE value is rejected (return undefined) if:
 *   1. Value contains ANY character outside the strict allowlist — this
 *      includes CR, LF, null bytes, spaces, slashes, control characters,
 *      angle brackets, etc. The allowlist /[^A-Za-z0-9\-_:.]/ covers all
 *      of these. Stripping invalid characters is NOT done because stripping
 *      could silently transform a malicious payload into a valid-looking ID
 *      (e.g. "req/../../admin" → "req..admin", or "req\r\nX-Injected:admin"
 *      → "reqX-Injected:admin" which still contains the injected text).
 *   2. Value looks like a credential (Bearer token, API key, etc.) — checked
 *      on the ORIGINAL value before any transformation.
 *   3. Value is empty or exceeds SAFE_ID_MAX_LEN.
 *
 * Note: Bearer token check must be performed on the ORIGINAL value because
 * the Bearer pattern requires whitespace, which would be absent after any
 * allowlist-based cleaning.
 */
export function sanitizeId(value) {
    if (value === undefined)
        return undefined;
    if (value.length === 0)
        return undefined;
    // Reject if value exceeds maximum length
    if (value.length > SAFE_ID_MAX_LEN)
        return undefined;
    // Reject if value contains ANY character outside the strict allowlist.
    // This single check covers CR, LF, null bytes, spaces, slashes, tabs,
    // control characters, angle brackets, and all other non-allowlisted chars.
    // Hard rejection — no stripping — to prevent silent payload transformation.
    if (/[^A-Za-z0-9\-_:.]/.test(value))
        return undefined;
    // Reject if value looks like a credential (checked on original value)
    if (looksLikeCredential(value))
        return undefined;
    return value;
}
/**
 * Build a SafeErrorLog from an unknown thrown error.
 * Reuses redactSensitiveData from audit/redaction.ts for nested objects.
 * Never includes raw stack, raw error.message, or credentials.
 * requestId/actorId/tenantId are strictly sanitized.
 */
export function buildSafeErrorLog(input) {
    const safeMessage = extractSafeMessage(input.error);
    const requestId = sanitizeId(input.requestId);
    const actorId = sanitizeId(input.actorId);
    const tenantId = sanitizeId(input.tenantId);
    const log = {
        errorCode: sanitizeErrorCode(input.errorCode),
        category: sanitizeCategory(input.category),
        operation: sanitizeOperation(input.operation),
        retryable: input.retryable ?? false,
        safeMessage,
        timestamp: new Date().toISOString(),
    };
    if (requestId !== undefined)
        log.requestId = requestId;
    if (actorId !== undefined)
        log.actorId = actorId;
    if (tenantId !== undefined)
        log.tenantId = tenantId;
    return log;
}
/**
 * Categories whose safeMessage is allowed in public responses.
 * Only user-safe validation errors may expose their message.
 */
const PUBLIC_SAFE_MESSAGE_CATEGORIES = new Set(["validation"]);
/**
 * Build a PublicErrorResponse from a SafeErrorLog.
 * Only exposes errorCode, a generic or explicitly-allowlisted message,
 * retryable, and sanitized requestId. Never exposes raw stack, operation,
 * actorId, tenantId, SQL, DSN, path or provider internals.
 *
 * Defensive re-sanitization: even though log.requestId was already sanitized
 * in buildSafeErrorLog, we re-run sanitizeId here as a defence-in-depth
 * measure in case a SafeErrorLog is constructed by other means (e.g. tests,
 * deserialization, or future code paths that bypass buildSafeErrorLog).
 */
export function buildPublicErrorResponse(log) {
    const allowSafeMessage = PUBLIC_SAFE_MESSAGE_CATEGORIES.has(log.category) && log.errorCode.startsWith("ERR_VALIDATION");
    const pub = {
        errorCode: log.errorCode,
        message: allowSafeMessage ? log.safeMessage : SAFE_PUBLIC_ERROR_GENERIC_MESSAGE,
        retryable: log.retryable,
    };
    // Defensive re-sanitization — never trust log.requestId blindly
    const safeRequestId = sanitizeId(log.requestId);
    if (safeRequestId !== undefined)
        pub.requestId = safeRequestId;
    return pub;
}
/**
 * Convenience builder for a public error response body directly from an input.
 * Returns { errorCode, error, retryable, requestId } using the conventional
 * `error` field name used by server/desktop APIs.
 */
export function buildPublicErrorBody(input) {
    const pub = buildPublicErrorResponse(buildSafeErrorLog(input));
    const body = {
        errorCode: pub.errorCode,
        error: pub.message,
        retryable: pub.retryable,
    };
    if (pub.requestId !== undefined)
        body.requestId = pub.requestId;
    return body;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function sanitizeErrorCode(code) {
    const clean = code.replace(/[^A-Z0-9_]/gi, "_").toUpperCase().slice(0, 64);
    return clean || "ERR_UNKNOWN";
}
function sanitizeCategory(category) {
    const allowed = [
        "validation", "provider", "auth", "internal", "network",
        "database", "audit", "approval", "emergency_stop", "voice", "desktop", "project_runtime", "unknown",
    ];
    const lower = category.toLowerCase();
    return allowed.includes(lower) ? lower : "unknown";
}
function sanitizeOperation(operation) {
    return redactInlineSecrets(operation).slice(0, 128);
}
function looksLikeCredential(value) {
    return SECRET_INLINE_PATTERNS.some((p) => {
        const test = new RegExp(p.source, p.flags);
        return test.test(value);
    }) || isSensitiveKey(value);
}
/**
 * Redact a context object for safe inclusion in an audit event.
 * Reuses the canonical redactSensitiveData from audit/redaction.ts.
 */
export function redactContextForAudit(context) {
    return redactSensitiveData(context);
}
// ---------------------------------------------------------------------------
// Console helpers — safe by construction
// ---------------------------------------------------------------------------
function emitSafeLog(level, input) {
    const errorCode = sanitizeErrorCode(input.errorCode);
    let line;
    try {
        line = safeJsonSerialize(buildSafeErrorLog(input));
    }
    catch {
        line = `SAFE_LOG_FAILED:${errorCode}`;
    }
    const logger = console[level];
    if (typeof logger === "function") {
        logger(line);
    }
}
/**
 * Emit a structured, redacted error log to console.error.
 * Never throws; on serialization failure it emits a minimal fallback line.
 */
export function safeConsoleError(input) {
    emitSafeLog("error", input);
}
/**
 * Emit a structured, redacted warning log to console.warn.
 * Never throws; on serialization failure it emits a minimal fallback line.
 */
export function safeConsoleWarn(input) {
    emitSafeLog("warn", input);
}
//# sourceMappingURL=safeErrorLog.js.map